// API 客户端：访问令牌只放内存，401 时用 httpOnly 刷新 cookie 单飞刷新一次后重试。
import type { TokenResponse } from "./types";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId?: string;
  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

/** 把任意错误转成给用户看的中文文案。 */
export function errorMessage(err: unknown, fallback = "操作失败，请稍后重试"): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof TypeError) return "网络连接失败，请检查网络后重试";
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

let accessToken: string | null = null;
let refreshing: Promise<TokenResponse | null> | null = null;
const listeners = new Set<(t: TokenResponse | null) => void>();

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/** 订阅令牌刷新结果（null = 刷新失败，会话已失效）。 */
export function onSessionChange(fn: (t: TokenResponse | null) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** 用 cookie 刷新访问令牌；并发调用共享同一次请求。 */
export function refreshSession(): Promise<TokenResponse | null> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, { method: "POST", credentials: "same-origin" });
        if (!res.ok) {
          accessToken = null;
          return null;
        }
        const body = (await res.json()) as TokenResponse;
        accessToken = body.access_token;
        return body;
      } catch {
        return null;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })().then((t) => {
      listeners.forEach((fn) => fn(t));
      return t;
    });
  }
  return refreshing;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: string;
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  /** 不附带访问令牌、不自动刷新（登录/注册等）。 */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: Query) {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = await res.json();
    const e = body?.error;
    if (e) return new ApiError(res.status, e.code ?? "error", e.message ?? res.statusText, e.details, e.request_id);
  } catch {
    /* 非 JSON */
  }
  return new ApiError(res.status, `http_${res.status}`, res.status >= 500 ? "服务暂时不可用，请稍后重试" : res.statusText);
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const doFetch = () => {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (!opts.anonymous && accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(buildUrl(path, opts.query), {
      method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      credentials: "same-origin",
    });
  };

  let res = await doFetch();
  if (res.status === 401 && !opts.anonymous) {
    const t = await refreshSession();
    if (t) res = await doFetch();
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204 || res.headers.get("Content-Length") === "0") return undefined as T;
  const ct = res.headers.get("Content-Type") ?? "";
  if (!ct.includes("json")) return undefined as T;
  return (await res.json()) as T;
}

export const get = <T>(path: string, query?: Query, signal?: AbortSignal) => api<T>(path, { query, signal });
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: "POST", body: body ?? {} });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
export const del = <T = void>(path: string) => api<T>(path, { method: "DELETE" });
